import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getActionDashboard } from '../api/dashboardApi'
import { isQueryReady, type DashboardQuery } from '../types'

/**
 * Yönetici Raporu sunucu-durumu (068). Panonun tüm bölümleri **bu tek sorgudan** beslenir → filtre
 * değişiminde hepsi birlikte yenilenir ve aralarında geçici tutarsızlık oluşmaz.
 *
 * <p><b>`i18n.language` queryKey'in parçasıdır:</b> yanıttaki ad alanları sunucuda `Accept-Language`'e
 * göre çözülür — dağılımın `statusName`'i (REFERANS), kişi bazlı yükün `jobTitle`'ı, kritik tablonun
 * `importanceName`'i. Dil TR↔EN değiştiğinde anahtar değişmezse önbellekten eski dildeki yanıt sunulur.
 *
 * <p><b>Otomatik yenileme: 30 saniye</b> (077 §K9 — kullanıcı isteği 2026-08-06). 068'de periyodik
 * yenileme bilinçli olarak yoktu ve tazeleme yalnız "Yenile" butonuyla olurdu; o buton Yönetici
 * Raporu'ndan kaldırıldığı için yerini bu aralık aldı. Değişimin görünen kanıtı üstteki "Son Güncelleme"
 * damgasıdır — sunucudan gelen `generatedAt` her turda ilerler.
 *
 * <p><b>Sekme arka plandayken durur:</b> `refetchIntervalInBackground` verilMEZ (TanStack varsayılanı
 * `false`) → görünmeyen bir sekme sunucuyu 30 saniyede bir dürtmez. `refetchOnWindowFocus` ise kapalı
 * kalır: odak her değiştiğinde ek bir istek atmak, zaten çalışan aralığın üstüne gereksiz yük olurdu.
 *
 * <p><b>Özel tarih aralığı yarım kaldığında istek atılmaz</b> ({@link isQueryReady}): kullanıcı
 * başlangıcı seçip bitişi seçmemişken sunucu 400 dönerdi; sorgu beklemeye alınır.
 *
 * <p>`placeholderData` **bilinçli olarak kullanılMAZ**: filtre değiştiğinde eski değerler yeni filtreye
 * aitmiş gibi gösterilemez → bölümler yükleme durumuna geçer. Buna karşılık <b>yenileme hatasında</b>
 * TanStack Query son başarılı veriyi ekranda tutar (aynı queryKey) → veri kaybı olmaz.
 */
export function useActionDashboard(query: DashboardQuery, enabled = true) {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: ['action-dashboard', i18n.language, ...queryKeyParts(query)],
    queryFn: () => getActionDashboard(query),
    enabled: enabled && isQueryReady(query),
    refetchOnWindowFocus: false,
    refetchInterval: DASHBOARD_REFRESH_MS,
    // Periyodik yoklama merkezî hata hunisinden (019) MUAFTIR: kullanıcı isteğiyle değil, arka planda
    // 30 saniyede bir çalışır ve başarısızlığın kullanıcıya görünen yüzeyi zaten satır-içi hata bandıdır
    // (`home-error`). Susturulmasaydı bir kesintide pano açık kaldığı sürece HER 30 SANİYEDE bir hata
    // toast'ı düşerdi — üstelik bandın üstüne. Aynı gerekçe ve aynı desen: `useUnreadCount` (058).
    meta: { suppressGlobalError: true },
  })
}

/**
 * Panoların otomatik tazeleme aralığı (077 §K9 — kullanıcı isteği "her 30 saniyede bir"). Testin ve
 * hook'ların <b>tek</b> kaynağı burasıdır.
 *
 * <p><b>İki panonun DÖRT sorgusu da bu sabiti kullanır</b> (2026-08-19): Yönetici Raporu'nun özeti ve
 * çalışma listesi, Raporum'un panosu ve "Aksiyon Beklenen Maddeler" tablosu. Değeri değiştiren kişi
 * dördünü birden değiştirmiş olur — bu <b>bilinçlidir</b>: bir panonun iki sorgusu ayrışırsa donut'lar
 * ile hemen altındaki tablo farklı anların verisini gösterir. Sabiti bölmek yerine, gerçekten pano
 * bazında farklı aralık isteniyorsa ikinci bir sabit adıyla ve gerekçesiyle tanımlanmalıdır.
 */
export const DASHBOARD_REFRESH_MS = 30_000

/** Sorgunun kimliğini oluşturan parçalar — iki hook aynı kuralı paylaşsın diye tek yerde. */
export function queryKeyParts(query: DashboardQuery): string[] {
  return [
    // Sicil listesi anahtara girmeden önce sıralanır (aynı küme farklı sırada gelse de aynı önbellek
    // anahtarı üretilsin). Karşılaştırıcı AÇIKÇA verilir: `sort()` varsayılanı belirtilmemiş sayılır
    // (Sonar typescript:S2871). Siciller salt rakam olduğundan sıra bu karşılaştırıcıyla da öncekiyle aynı.
    query.employeeIds.length === 0
      ? 'all'
      : [...query.employeeIds].sort((a, b) => a.localeCompare(b)).join(','),
    query.period,
    query.startDate ?? '',
    query.endDate ?? '',
  ]
}
